import type { Device, ScheduleView, Sensor, WeatherData } from '../types/device'

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message)
  }
}

/** Custom event the AuthProvider listens to so it can drop the user back to the login page. */
export const AUTH_EXPIRED_EVENT = 'act:auth-expired'

async function request<T>(method: string, path: string, body?: unknown, opts?: { skipAuthEvent?: boolean }): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin', // ensure the session cookie rides along on /api/* calls
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  const json = text ? JSON.parse(text) : undefined
  if (!res.ok) {
    // Globally notify the AuthProvider on session expiry / not-logged-in so
    // it can flip back to the login page. Skip for explicit auth probes
    // (e.g. /api/auth/me) where 401 is the expected "no session" signal.
    if (res.status === 401 && !opts?.skipAuthEvent) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    }
    throw new ApiError(res.status, json?.error ?? `HTTP ${res.status}`, json?.code)
  }
  return json as T
}

export const api = {
  listDevices: () => request<{ devices: Device[] }>('GET', '/api/devices'),
  deviceStatus: (id: string) => request<{ device: Device }>('GET', `/api/devices/${id}/status`),
  controlDevice: (id: string, body: { on: boolean; channel?: number }) =>
    request<{ ok: true }>('POST', `/api/devices/${id}/control`, body),
  getSchedules: (id: string) => request<ScheduleView>('GET', `/api/devices/${id}/schedules`),
  saveSchedules: (id: string, body: ScheduleView) =>
    request<{ ok: true; onJobId?: number; offJobId?: number }>('POST', `/api/devices/${id}/schedules`, body),
  getWeather: () => request<WeatherData>('GET', '/api/weather'),
  listSensors: () => request<{ sensors: Sensor[]; note?: string }>('GET', '/api/sensors'),
  // Auth — `me` is silent on 401 so we can probe for an existing session
  // without flipping the AuthProvider's "expired" handler. Login is a browser
  // redirect (GET /api/auth/login → Keycloak), so it's not in this client.
  authMe: () =>
    request<{ user: string; isAdmin: boolean }>('GET', '/api/auth/me', undefined, { skipAuthEvent: true }),
  authLogout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  /** Confirm the admin PIN. Elevates the session for 5 minutes. */
  verifyPin: (pin: string) => request<{ ok: true }>('POST', '/api/auth/verify-pin', { pin }),
  /** Change the shared admin PIN. Admin role required. */
  changePin: (body: { currentPin: string; newPin: string }) =>
    request<{ ok: true }>('POST', '/api/auth/change-pin', body),
}
